import React, { useState, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Button as MtButton, Snackbar, Alert } from '@mui/material';
import { find } from 'lodash';
import { handleAiAutoGradingSchedule } from 'store/submissions/thunks';
import { useUserProfileConfig } from 'store/users/selectors';
import { useAlert } from 'components/UI/alertSakeBar/alertProvider';
import { getDiffConfigForStudent } from 'utils/methods/math';
import * as Sentry from '@sentry/react';
import Loading from './Loading';
import AiGradingLevelModal from './Modals/AiGradingLevelModal';

const isGradeLevelUuid = (value) =>
    typeof value === 'string' &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);

const AiGradingButton = ({
    assignment,
    show_ai_grade_button,
    is_graphic_organizer,
    answersLength,
    allAssignment,
    selected_student_id
}) => {
    const [loading, setLoading] = useState(false);
    const dispatch = useDispatch();
    const [alertData, setAlertData] = useState({
        open: false,
        message: ''
    });
    const [aiGradeModalOpen, setAiGradeModalOpen] = useState(false);
    const { isAiPermissionEnabled } = useUserProfileConfig();
    const showAlert = useAlert();

    const getRawGradeLevel = useMemo(() => {
        let grade_level = null;
        
        if (assignment?.ai_grade && Array.isArray(assignment.ai_grade) && assignment.ai_grade.length > 0) {
            const firstAiGrade = assignment.ai_grade[0];
            if (firstAiGrade?.grade_level && !isGradeLevelUuid(firstAiGrade.grade_level)) {
                grade_level = firstAiGrade.grade_level;
            }
        }
        
        if (!grade_level) {
            const class_act = find(allAssignment, v => v?.data.id == assignment?.assignmentId || v?.data?.id == assignment?.assignment?.id) || {};
            
            if (selected_student_id && class_act.classId) {
                const groupConfig = getDiffConfigForStudent(selected_student_id, class_act, class_act.classId);
                if (groupConfig?.ai_grading_config?.grade_level) {
                    grade_level = groupConfig.ai_grading_config.grade_level;
                }
            }
            
            if (!grade_level) {
                const classConfig = class_act.classes?.find(c => c.id === class_act.classId);
                const ai_grading_config = class_act.ai_grading_config || classConfig?.ai_grading_config || class_act.data?.ai_grading_config || { creteria: {} };
                grade_level = ai_grading_config.creteria?.grade_level || ai_grading_config.grade_level;
            }
        }
        
        return grade_level || 'Advance';
    }, [allAssignment, assignment, selected_student_id]);

    const callForAiFeedback = async (grade_level = null) => {
        if (!isAiPermissionEnabled) {
            showAlert('Please enable AI permission to generate content using AI.', 'error');
            return;
        }
        const submission_id = assignment?.id;
        if (submission_id) {
            try {
                setLoading(true);
                await dispatch(handleAiAutoGradingSchedule(submission_id, null, true, true, grade_level));
                setAlertData({ 
                    open: true, 
                    message: `Your assignment has been submitted for AI grading. Please allow up to 15 minutes for the process to complete and check back afterward. Thank you for your patience!` 
                });
                setAiGradeModalOpen(false);
            } catch (error) {
                Sentry.captureException(`211 Error in callForAiFeedback: ${error}`);
                console.log(error.message, "error");
            } finally {
                setLoading(false);
            }
        }
    };

    const handleOpenAiGradeModal = () => {
        setAiGradeModalOpen(true);
    };

    const handleCloseAiGradeModal = () => {
        setAiGradeModalOpen(false);
    };

    const handleSubmitAiGrade = (gradeLevel) => {
        callForAiFeedback(gradeLevel);
    };

    const handleClose = () => {
        setAlertData({ open: false, message: '' });
    };

    if (!show_ai_grade_button || is_graphic_organizer || answersLength === 0) {
        return null;
    }

    return (
        <>
            <MtButton 
                variant="contained" 
                size="small"
                startIcon={
                    <span style={{ 
                        display: 'inline-flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        width: '12px',
                        height: '12px',
                        minWidth: '12px'
                    }}>
                        {loading && <Loading white size={12} />}
                    </span>
                }
                onClick={handleOpenAiGradeModal}
                disabled={loading}
                sx={{ 
                    whiteSpace: 'nowrap',
                    fontSize: '13px',
                    padding: '6px 16px',
                    background: 'linear-gradient(to right, #7b2cbf, #9d4edd)',
                    color: 'white',
                    border: 'none',
                    '&:hover': {
                        background: 'linear-gradient(to right, #6a1b9a, #8e24aa)',
                    },
                    '&:disabled': {
                        background: 'linear-gradient(to right, #7b2cbf, #9d4edd)',
                        opacity: 0.6
                    },
                    '& .MuiButton-startIcon': {
                        marginRight: 0.75,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '12px',
                        minWidth: '12px'
                    }
                }}
            >
                Solicitar calificación de IA
            </MtButton>
            <AiGradingLevelModal
                open={aiGradeModalOpen}
                onClose={handleCloseAiGradeModal}
                onSubmit={handleSubmitAiGrade}
                defaultGradeLevel={getRawGradeLevel}
                loading={loading}
            />
            <Snackbar
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                open={alertData.open}
                autoHideDuration={5000}
                onClose={handleClose}
            >
                <Alert onClose={handleClose} severity='info'>
                    <strong>ALERT:</strong> {alertData.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default AiGradingButton;
